import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { Disclaimer } from "@/components/Disclaimer";
import { api, money, weekly } from "@/lib/api";
import { Loader2 } from "lucide-react";

const LENDERS = [
  "Commonwealth Bank", "Westpac", "NAB", "ANZ", "Macquarie Bank",
  "ING", "Suncorp Bank", "Bankwest", "St.George Bank", "Bendigo Bank",
  "ubank", "Bank of Queensland", "AMP Bank", "Great Southern Bank", "Newcastle Permanent",
];
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="label-eyebrow text-mutedText">{label}</label>
    <div className="mt-1.5 flex items-center rounded-xl border border-line bg-white/5 px-3 focus-within:ring-2 focus-within:ring-brand">
      <span className="text-mutedText text-sm">$</span>
      <input {...props} type="number" className="w-full bg-transparent px-2 py-3 text-forest focus:outline-none" />
    </div>
  </div>
);

export default function Finance() {
  const { session, data, next, back, mergeData } = useJourney();
  const [f, setF] = useState(() => {
    const fi = data.finance_inputs;
    if (fi) return {
      annual_income: fi.annual_income ?? "",
      partner_income: fi.partner_income ?? "",
      has_partner: !!fi.has_partner,
      monthly_commitments: fi.monthly_commitments ?? "",
      deposit: fi.deposit ?? "",
      dependants: fi.dependants ?? 0,
    };
    return {
      annual_income: "", partner_income: "", has_partner: false,
      monthly_commitments: "", deposit: "", dependants: 0,
    };
  });
  const [result, setResult] = useState(data.finance || null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const lenderRow = useMemo(() => shuffle(LENDERS), []);

  const calc = async () => {
    setLoading(true);
    try {
      const payload = {
        annual_income: Number(f.annual_income) || 0,
        partner_income: Number(f.partner_income) || 0,
        has_partner: f.has_partner,
        monthly_commitments: Number(f.monthly_commitments) || 0,
        deposit: Number(f.deposit) || 0,
        dependants: Number(f.dependants) || 0,
      };
      const r = await api.finance(session.id, payload);
      setResult(r);
      mergeData({ finance: r, finance_inputs: payload });
      api.patchSession(session.id, { data: { finance_inputs: payload } }).catch(() => {});
    } catch (e) {
      toast.error("Could not calculate. Check the inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StageFrame testId="stage-finance" className="flex flex-col lg:flex-row pt-16 pb-28">
      <div className="lg:w-[42%] px-6 md:px-12 py-8 border-r border-line">
        <Eyebrow>Finance Snapshot · Indicative</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-forest mt-3">
          A quick look at what's possible.
        </h1>
        <p className="text-mutedText mt-3 text-sm">Since most people think weekly.</p>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <Field label="Your income (annual)" data-testid="finance-income" value={f.annual_income} onChange={(e) => set("annual_income", e.target.value)} placeholder="95000" />
          <Field label="Partner income" data-testid="finance-partner-income" value={f.partner_income} onChange={(e) => set("partner_income", e.target.value)} placeholder="0" />
          <Field label="Monthly commitments" data-testid="finance-commitments" value={f.monthly_commitments} onChange={(e) => set("monthly_commitments", e.target.value)} placeholder="600" />
          <Field label="Deposit saved" data-testid="finance-deposit" value={f.deposit} onChange={(e) => set("deposit", e.target.value)} placeholder="60000" />
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-line bg-white/5 px-4 py-3">
          <span className="text-sm text-forest">Buying with a partner?</span>
          <button data-testid="finance-partner-toggle" onClick={() => set("has_partner", !f.has_partner)}
            className={`h-6 w-11 rounded-full transition ${f.has_partner ? "bg-brand" : "bg-line"}`}>
            <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${f.has_partner ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div className="mt-4">
          <label className="label-eyebrow text-mutedText">Dependants</label>
          <div className="mt-2 flex items-center gap-2">
            {[0, 1, 2, 3, 4].map((n) => (
              <button key={n} data-testid={`finance-dependants-${n}`} onClick={() => set("dependants", n)}
                className={`h-10 w-10 rounded-full border text-sm transition ${f.dependants === n ? "border-brand bg-brand text-white" : "border-line text-forest/70"}`}>{n}</button>
            ))}
          </div>
        </div>

        <button data-testid="finance-calc-btn" onClick={calc} disabled={loading}
          className="mt-6 flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-white text-sm font-medium transition hover:bg-brand disabled:opacity-50 w-full">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Calculating…</> : "See indicative range"}
        </button>
      </div>

      <div className="lg:w-[58%] px-6 md:px-14 py-10 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {result ? (
            <motion.div key="res" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} data-testid="finance-result">
              <Eyebrow>Indicative weekly repayment</Eyebrow>
              <div className="font-serif font-light text-brand leading-none mt-2" style={{ fontSize: "clamp(4rem, 11vw, 9rem)" }} data-testid="finance-repayment-hero">
                {weekly(result.indicative_repayment_weekly)}<span className="text-3xl md:text-4xl text-forest/50">/wk</span>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-x-10 gap-y-5 max-w-lg">
                <div>
                  <span className="label-eyebrow text-mutedText">Indicative borrowing</span>
                  <p className="font-serif text-2xl text-forest mt-1">{money(result.borrowing_low)} – {money(result.borrowing_high)}</p>
                </div>
                <div>
                  <span className="label-eyebrow text-mutedText">Your deposit</span>
                  <p className="font-serif text-2xl text-forest mt-1">{money(result.deposit)}</p>
                </div>
                <div>
                  <span className="label-eyebrow text-mutedText">Assessed at</span>
                  <p className="text-forest mt-1">{result.assessment_rate}% over {result.term_years} yrs</p>
                </div>
                <div>
                  <span className="label-eyebrow text-mutedText">Monthly repayment</span>
                  <p className="text-forest mt-1">{money(result.indicative_repayment_monthly)}/mo</p>
                </div>
                <div>
                  <span className="label-eyebrow text-mutedText">Living costs (HEM)</span>
                  <p className="text-forest mt-1">{money(result.assessed_living)}/mo</p>
                </div>
              </div>
              <Disclaimer className="mt-8 max-w-lg" />

              <div className="mt-8 max-w-lg" data-testid="finance-lenders">
                <span className="label-eyebrow text-mutedText">Lenders competing for you</span>
                <div className="mt-2 overflow-hidden rounded-xl border border-line bg-white/5 py-2">
                  <Marquee speed={30} gradient={false}>
                    {lenderRow.map((l, i) => (
                      <span key={l + i} className="mx-3 inline-flex items-center gap-2 text-sm text-forest/85 whitespace-nowrap">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand" />{l}
                      </span>
                    ))}
                  </Marquee>
                </div>
                <p className="text-xs text-mutedText mt-2">Dozens of lenders — we shop for the sharpest weekly repayment for you.</p>
              </div>
            </motion.div>
          ) : (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-mutedText/70">
              <p className="font-serif text-2xl">Enter a few details to reveal an indicative repayment.</p>
              <Disclaimer short className="mt-6 justify-center" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ConsultantNav testId="stage-finance" onBack={back} onNext={result ? next : null} nextLabel="Build the budget" disabled={!result} />
    </StageFrame>
  );
}
